import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';

export const useAdminNotifications = (adminEmail) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const channelRef = useRef(null);

  useEffect(() => {
    if (!adminEmail) {
      setLoading(false);
      return;
    }

    const fetchNotifications = async () => {
      try {
        const { data, error } = await supabase
          .from('admin_notifications')
          .select('*')
          .eq('admin_email', adminEmail.toLowerCase())
          .order('created_at', { ascending: false });
        if (error) {
          // Silently ignore if table doesn't exist
          if (error.code === 'PGRST205') {
            setNotifications([]);
            setUnreadCount(0);
            return;
          }
          throw error;
        }
        setNotifications(data || []);
        setUnreadCount((data || []).filter(n => !n.read).length);
      } catch (err) {
        // Only log real errors, not missing table
        if (err.code !== 'PGRST205') {
          console.error('Error fetching admin notifications:', err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();

    channelRef.current = supabase
      .channel(`admin-notifications-${adminEmail}`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'admin_notifications',
        filter: `admin_email=eq.${adminEmail.toLowerCase()}`,
      }, () => fetchNotifications())
      .subscribe();

    return () => {
      if (channelRef.current) supabase.removeChannel(channelRef.current);
    };
  }, [adminEmail]);

  const markAsRead = async (notificationId) => {
    try {
      await supabase.from('admin_notifications')
        .update({ read: true })
        .eq('id', notificationId);
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const unread = notifications.filter(n => !n.read);
      if (!unread.length) return;
      await supabase.from('admin_notifications')
        .update({ read: true })
        .eq('admin_email', adminEmail.toLowerCase())
        .eq('read', false);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  return { notifications, unreadCount, loading, markAsRead, markAllAsRead };
};
